import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useComponentTracking } from '../../../utils/component-events'
import { brand } from '../../../redux/app-selectors'
import { FsCore } from '../../../types/fs-core'
import { setConfig } from '../../helpers/consent'
import { RegisterOptions, useForm, Ref, FieldValues, CustomElement } from 'react-hook-form'
import CallOut from '../../callout/ev-callout'
import ProfileInformation from '../form-helpers/profile-information'
import { maskPII } from '../../../utils/form'
import uuidv4 from '../../../utils/guid'
import { readCookieRaw } from '../../../utils/cookies'
import { Icon } from '../../../atoms'

export type RegisterTrack = (
  ref:
    | ((HTMLSelectElement & HTMLTextAreaElement & CustomElement<FieldValues> & HTMLInputElement) &
        Ref)
    | null,
) => void

interface SMEformProps {
  brand: 'money' | 'uswitch' | 'confused'
  sys: {
    id: string
  }
  name: string
}
const { __FS_API_URL__ } = global
const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'
const formName = 'Journey form'

const debounce: Record<string, number> = {}
const SMEform: React.FC<SMEformProps> = ({ brand }) => {
  const { register, errors, setValue, watch, unregister, control, setError, clearErrors } = useForm(
    {
      mode: 'onChange',
      defaultValues: {
        businessDetails: {
          businessType: 'limitedCompany',
        },
        directorDetails: {
          emailAddress: undefined,
        },
      },
    },
  )

  const businessDetails = watch('businessDetails')

  const [consents, setConsents] = useState({ marketing: true })
  const [landingPage, setLandingPage] = useState<string | null>(null)
  const [isForcedJourneyTest, setIsForcedJourneyTest] = useState(false)

  const trackComponent: (
    eventName: string,
    subEventName: string,
    payload: Record<string, any>,
  ) => void = useComponentTracking()

  const piiData = [
    'emailAddress',
    'address_line_1',
    'address_line_2',
    'postal_code',
    'locality',
    'country',
  ]

  const registerWithTrack = (validation: RegisterOptions, detail: string): RegisterTrack => {
    return register({
      ...validation,
      validate: (value: any) => {
        clearTimeout(debounce[detail])
        debounce[detail] = setTimeout(() => {
          trackComponent('Form Event', 'formFieldInteracted', {
            formStep: detail,
            selected: piiData.includes(detail) ? 'untracked' : value,
          })
        }, 1000)
        return true
      },
    })
  }

  useEffect(() => {
    setConfig({
      apiPath: window.location.href.includes('staging')
        ? 'https://www.staging.money.co.uk/consent'
        : 'https://www.money.co.uk/consent',
      clientId: 'fs-core',
      brand: 'money',
    })
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location?.search)
      const landingPageParam = urlSearchParams.get('landingPage')
      if (landingPageParam) {
        setLandingPage(landingPageParam)
        sessionStorage.setItem('landingPage', landingPageParam)
      }
      if (window.location?.href?.includes('/business/finance-journey')) {
        setIsForcedJourneyTest(true)
      }
    }
  }, [])

  const handleSubmit = async (finalBody: Object) => {
    const res = await fetch(
      `${__FS_API_URL__}/form/sme?brand=${brand}&deviceId=${readCookieRaw(
        'uscd',
      )}&landingPage=${landingPage}&requestId=${uuidv4()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalBody),
      },
    )
    await Promise.all([
      trackComponent('Form Event', 'sectionCompleted', {
        SectionName: 'Profile Information',
        FormName: formName,
        FieldData: maskPII(finalBody, ['businessAddress', 'email']),
        status: res.ok ? 'Success' : 'Failed',
      }),
      trackComponent('Form Event', 'formSubmitted', {
        formName: formName,
      }),
    ]).catch(() => {
      // eslint-disable-next-line no-console
      console.log('SME Journey form tracking failed')
    })
    window.location.href = '/business/journey/results'
  }

  const handleForcedJourneySubmit = async (finalBody) => {
    const res = await fetch(
      `${apiPath}/sme-profile?brand=money&deviceId=${readCookieRaw('uscd')}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalBody),
      },
    )
    await Promise.all([
      trackComponent('Form Event', 'sectionCompleted', {
        SectionName: 'Profile Information',
        FormName: formName,
        FieldData: maskPII(finalBody, ['businessAddress', 'email']),
        status: res.ok ? 'Success' : 'Failed',
      }),
      trackComponent('Form Event', 'formSubmitted', {
        formName: formName,
      }),
    ]).catch(() => {
      // eslint-disable-next-line no-console
      console.log('SME Journey form tracking failed')
    })
    if (businessDetails.businessType === 'limitedCompany') {
      if (parseInt(businessDetails.turnover) >= 10000) {
        // Hardcoding this link for forced journey test (1 week test) to track revenue via clk tracker. https://rvu.atlassian.net/browse/MSME-504
        window.location.href =
          'https://www.money.co.uk/clk/75a51b69-a4dd-4af7-8872-adb3e692696a-2558150265cee8643cf723affdf96aef644bfbba/money/business-loans?r=https%3A%2F%2Fwww.money.co.uk%2Fbusiness%2Fbusiness-loans%2Fapplication%2Fiwoca&category=business-loans&supplier=iwoca&product_name=iwoca+Business+Loan&pid=75a51b69-a4dd-4af7-8872-adb3e692696a&ln=application&upvid=75a51b69-a4dd-4af7-8872-adb3e692696a-2558150265cee8643cf723affdf96aef644bfbba&ucvid=1000033063&ulvid=961336&link_id=27678d18-5d2e-4463-b3cc-6bbddbe9c5c4&sig=0babd00c33e23afa0700a791c0895f95b37d1f93bd702ac413490994fb51360b'
      } else {
        window.location.href = '/business/business-loans/alternate-finance-options'
      }
    } else {
      window.location.href = '/business/business-loans/sole-traders'
    }
  }

  return (
    <div className='SMEform'>
      <h3>Tell us about your business</h3>
      {isForcedJourneyTest && (
        <div className='SMEform__infoBox SMEform__infoBox--mintGreen'>
          {<Icon glyph='light-bulb' size={32} />}
          <div>{'Explore business loan options with no impact on your credit score.'}</div>
        </div>
      )}
      <form>
        <ProfileInformation
          registerWithTrack={registerWithTrack}
          register={register}
          setValue={setValue}
          unregister={unregister}
          businessDetails={watch('businessDetails')}
          directorDetails={watch('directorDetails')}
          hideButton={false}
          consents={consents}
          setConsents={setConsents}
          setError={setError}
          clearErrors={clearErrors}
          errors={errors}
          brand={brand}
          formName={formName}
          ctaText='Submit'
          landingPage={landingPage}
          handleSubmit={isForcedJourneyTest ? handleForcedJourneySubmit : handleSubmit}
          control={control}
          consentSource='profiling-journey'
        />
      </form>
      {businessDetails.businessType === 'limitedCompany' && businessDetails.turnover >= 100000 && (
        <div className='SMEform__callout'>
          <CallOut
            text={
              "The information you provide will be used for a 'soft credit search', visible to you but not to lenders and it will have no impact on your credit score."
            }
            bold={true}
          />
        </div>
      )}
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    ...props,
  }
})(SMEform)
